import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, MessageCircle } from "lucide-react";
import { Reveal, SectionHead } from "../components/Reveal";
import {
    ADMIN_BRACKETS,
    ADMIN_MIN,
    FEE_BRACKETS,
    FEE_MIN,
    WHATSAPP_NUMBER,
    estimateByBrackets,
} from "../data/content";

const SLIDER_MIN = 100000;
const SLIDER_MAX = 20000000;
const QUICK_VALUES = [
    { label: "R$ 250 mil", value: 250000 },
    { label: "R$ 1 mi", value: 1000000 },
    { label: "R$ 5 mi", value: 5000000 },
    { label: "R$ 15 mi", value: 15000000 },
];

const fmt = (v) =>
    v.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 0,
    });

export const Calculator = () => {
    const [raw, setRaw] = useState("1500000");
    const [arbitros, setArbitros] = useState(3);

    const value = useMemo(() => {
        const n = parseInt(raw || "0", 10);
        return Number.isFinite(n) ? Math.min(Math.max(n, 0), 999999999999) : 0;
    }, [raw]);

    const admin = estimateByBrackets(value, ADMIN_BRACKETS, value > 0 ? ADMIN_MIN : 0);
    const honorarios =
        estimateByBrackets(value, FEE_BRACKETS, value > 0 ? FEE_MIN : 0) * arbitros;
    const total = admin + honorarios;

    const sliderValue = Math.min(Math.max(value, SLIDER_MIN), SLIDER_MAX);
    const fill = ((sliderValue - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100;

    const whatsappSimUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        `Olá! Fiz uma simulação na calculadora da CAJUSP: valor da causa de ${fmt(
            value,
        )}, com ${arbitros === 3 ? "tribunal arbitral (3 árbitros)" : "árbitro único"}. Total estimado de ${fmt(
            total,
        )}. Gostaria de receber uma simulação detalhada.`,
    )}`;

    return (
        <section id="calculadora" data-testid="calculator-section" className="bg-graphite-deep py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHead
                    dark
                    eyebrow="Calculadora de custas arbitrais"
                    title="Estime as custas do procedimento em segundos"
                    lead="Informe o valor da causa e a composição do julgamento para obter uma estimativa transparente da taxa de administração e dos honorários arbitrais."
                />

                <div className="mt-16 grid gap-8 lg:grid-cols-2">
                    <Reveal className="border border-graphite-line bg-graphite-dark p-8 sm:p-10">
                        <label
                            htmlFor="calculator-value-input"
                            className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold-light"
                        >
                            Valor da causa
                        </label>
                        <div className="mt-4 flex items-baseline gap-2 border-b border-graphite-line pb-4 focus-within:border-gold">
                            <span className="font-serif text-2xl text-graphite-mute">R$</span>
                            <input
                                id="calculator-value-input"
                                data-testid="calculator-value-input"
                                type="text"
                                inputMode="numeric"
                                value={value ? value.toLocaleString("pt-BR") : ""}
                                onChange={(e) =>
                                    setRaw(e.target.value.replace(/\D/g, "").slice(0, 12))
                                }
                                placeholder="0"
                                className="w-full bg-transparent font-serif text-4xl text-cream outline-none placeholder:text-graphite-mute/50"
                                aria-label="Valor da causa em reais"
                            />
                        </div>

                        <input
                            type="range"
                            data-testid="calculator-value-slider"
                            className="range-gold mt-8"
                            min={SLIDER_MIN}
                            max={SLIDER_MAX}
                            step={50000}
                            value={sliderValue}
                            style={{ "--fill": `${fill}%` }}
                            onChange={(e) => setRaw(e.target.value)}
                            aria-label="Ajustar valor da causa"
                        />
                        <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-graphite-mute">
                            <span>R$ 100 mil</span>
                            <span>R$ 20 mi</span>
                        </div>

                        <div className="mt-8 flex flex-wrap gap-2.5">
                            {QUICK_VALUES.map((q) => (
                                <button
                                    key={q.value}
                                    type="button"
                                    data-testid={`calculator-quick-${q.value}`}
                                    onClick={() => setRaw(String(q.value))}
                                    className={`border px-4 py-2 font-mono text-[11px] tracking-[0.1em] transition-colors ${
                                        value === q.value
                                            ? "border-gold bg-gold/10 text-gold-light"
                                            : "border-graphite-line text-graphite-mute hover:border-gold/60 hover:text-cream"
                                    }`}
                                >
                                    {q.label}
                                </button>
                            ))}
                        </div>

                        <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.28em] text-gold-light">
                            Composição do julgamento
                        </p>
                        <div className="mt-4 grid grid-cols-2 gap-px border border-graphite-line bg-graphite-line">
                            <button
                                type="button"
                                data-testid="calculator-arbitro-unico"
                                onClick={() => setArbitros(1)}
                                className={`px-4 py-4 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
                                    arbitros === 1
                                        ? "bg-gold text-graphite-deep"
                                        : "bg-graphite-dark text-graphite-mute hover:text-cream"
                                }`}
                            >
                                Árbitro único
                            </button>
                            <button
                                type="button"
                                data-testid="calculator-tribunal"
                                onClick={() => setArbitros(3)}
                                className={`px-4 py-4 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
                                    arbitros === 3
                                        ? "bg-gold text-graphite-deep"
                                        : "bg-graphite-dark text-graphite-mute hover:text-cream"
                                }`}
                            >
                                Tribunal (3 árbitros)
                            </button>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12} className="flex flex-col border border-gold/30 bg-graphite-dark p-8 sm:p-10">
                        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold-light">
                            Estimativa do procedimento
                        </p>

                        <div className="mt-8 space-y-6">
                            <div className="flex items-baseline justify-between gap-4 border-b border-graphite-line pb-5">
                                <span className="text-sm text-graphite-mute">
                                    Taxa de administração
                                </span>
                                <span
                                    data-testid="calculator-admin-fee"
                                    className="font-mono text-lg text-cream"
                                >
                                    {fmt(admin)}
                                </span>
                            </div>
                            <div className="flex items-baseline justify-between gap-4 border-b border-graphite-line pb-5">
                                <span className="text-sm text-graphite-mute">
                                    Honorários arbitrais{" "}
                                    <span className="block text-xs text-graphite-mute/70">
                                        {arbitros === 3
                                            ? "tribunal arbitral · 3 árbitros"
                                            : "árbitro único"}
                                    </span>
                                </span>
                                <span
                                    data-testid="calculator-arb-fee"
                                    className="font-mono text-lg text-cream"
                                >
                                    {fmt(honorarios)}
                                </span>
                            </div>
                        </div>

                        <div className="mt-8">
                            <p className="text-sm text-graphite-mute">Total estimado</p>
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={total}
                                    data-testid="calculator-total"
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.25 }}
                                    className="mt-2 font-serif text-5xl text-gold-light sm:text-6xl"
                                >
                                    {fmt(total)}
                                </motion.p>
                            </AnimatePresence>
                        </div>

                        <div className="mt-auto pt-10">
                            <a
                                href={whatsappSimUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="calculator-whatsapp-cta"
                                className="inline-flex w-full items-center justify-center gap-3 rounded-sm bg-gold px-6 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-graphite-deep transition-colors hover:bg-gold-light"
                            >
                                <MessageCircle size={16} strokeWidth={2.2} />
                                Receber simulação detalhada
                            </a>
                            <p className="mt-6 flex gap-2.5 text-xs leading-relaxed text-graphite-mute">
                                <Info size={14} className="mt-0.5 shrink-0 text-gold" />
                                Simulação meramente estimativa, com base em tabela de referência
                                provisória e faixas regressivas. Os valores definitivos seguem o
                                Regulamento e a Tabela de Custas da CAJUSP. Não inclui despesas
                                eventuais do procedimento.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
